import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import Button from "../UI/Button";
import useTypewriter from "../../hooks/useTypewriter";

const Hero = ({ startTyping, isVideoReady }) => {
  const [showMainTitle, setShowMainTitle] = useState(false);
  const [showSubtitle, setShowSubtitle] = useState(false);
  const [showButton, setShowButton] = useState(false);
  const buttonRef = useRef(null);
  const [buttonPosition, setButtonPosition] = useState({ x: 0, y: 0 });

  const fullSubtitle =
    "Perfect for creators of all kinds — no animation skills needed.";
  const typedSubtitle = useTypewriter(fullSubtitle, 50, startTyping);

  useEffect(() => {
    if (isVideoReady) {
      // Staggered reveal animation
      const titleTimer = setTimeout(() => {
        setShowMainTitle(true);
      }, 300);
      
      const subtitleTimer = setTimeout(() => {
        setShowSubtitle(true);
      }, 600);
      
      const buttonTimer = setTimeout(() => {
        setShowButton(true);
      }, 900);
      
      return () => {
        clearTimeout(titleTimer);
        clearTimeout(subtitleTimer);
        clearTimeout(buttonTimer);
      };
    }
  }, [isVideoReady]);

  // Magnetic button effect
  const handleMouseMove = (e) => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const deltaX = (e.clientX - centerX) * 0.15;
    const deltaY = (e.clientY - centerY) * 0.15;
    setButtonPosition({ x: deltaX, y: deltaY });
  };

  const handleMouseLeave = () => {
    setButtonPosition({ x: 0, y: 0 });
  };

  return (
    <section
      className={`transition-opacity duration-1000 ease-in ${
        isVideoReady ? "opacity-100" : "opacity-0"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col items-center justify-center text-center">
          {/* Main Title with enhanced animation */}
          <h1
            className={`text-5xl sm:text-6xl md:text-6xl lg:text-6xl xl:text-7xl 2xl:text-8xl font-sourceSans font-black tracking-tighter text-white mb-3 sm:mb-4 md:mb-5 lg:mb-6 transition-all duration-1000 ease-out ${
              showMainTitle
                ? "opacity-100 translate-y-0 blur-0"
                : "opacity-0 translate-y-12 blur-sm"
            }`}
          >
            <span className="inline-block hover:scale-105 transition-transform duration-300">Bring Your Videos</span>
            <span 
              className={`block text-ag-green font-sourceSans tracking-tighter font-light italic transition-all duration-700 delay-200 ${
                showMainTitle ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"
              }`}
            >
              to Life Fast!
            </span>
          </h1>

          {/* Subtitle with fade effect */}
          <p
            className={`text-sm sm:text-base md:text-xl lg:text-2xl xl:text-2xl 2xl:text-3xl font-bold text-gray-500 font-sourceSans font-normal mb-4 sm:mb-5 md:mb-6 lg:mb-7 xl:mb-8 max-w-xs sm:max-w-md md:max-w-xl lg:max-w-2xl xl:max-w-3xl mx-auto h-12 sm:h-14 md:h-16 lg:h-20 xl:h-24 transition-all duration-700 ${
              showSubtitle ? "opacity-100" : "opacity-0"
            }`}
          >
            {typedSubtitle}
            {startTyping && typedSubtitle.length < fullSubtitle.length && (
              <span className="animate-pulse text-ag-green">|</span>
            )}
          </p>
          
          {/* CTA Button with magnetic effect and scale animation */}
          <Link to="/templates">
            <div 
              ref={buttonRef}
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
              className={`px-1.5 sm:px-2 py-1.5 sm:py-2 bg-ag-black-600 rounded-lg sm:rounded-xl hover:bg-ag-black-800 border border-ag-black-400 transition-all duration-300 hover:border-ag-green hover:shadow-lg hover:shadow-ag-green/20 ${
                showButton 
                  ? "opacity-100 scale-100 translate-y-0" 
                  : "opacity-0 scale-90 translate-y-4"
              }`}
              style={{
                transform: `translate(${buttonPosition.x}px, ${buttonPosition.y}px) scale(${showButton ? 1 : 0.9})`,
                transition: buttonPosition.x === 0 && buttonPosition.y === 0 
                  ? "transform 0.5s ease-out, opacity 0.5s ease-out, background-color 0.3s, border-color 0.3s, box-shadow 0.3s" 
                  : "background-color 0.3s, border-color 0.3s, box-shadow 0.3s",
              }}
            >
              <Button 
                className="w-full sm:w-auto text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl 2xl:text-3xl font-sourceSans tracking-tighter font-bold ripple-effect btn-press" 
                variant="special"
              >
                Try for free
              </Button>
            </div>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default Hero;
